import React, { useEffect, useState } from "react";
import { PageHeader, Card, Badge, Button, Spinner, Input } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Activity, Play, Sparkles } from "lucide-react";
import { LineChart, Line, AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";

export default function Risk() {
  const [analyses, setAnalyses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState({ title: "Portfolio VaR — " + new Date().toLocaleDateString(), portfolio_value: 10000000, simulations: 10000 });

  const load = async () => {
    const r = await endpoints.risk();
    setAnalyses(r.data);
    if (!selected && r.data.length) setSelected(r.data[0]);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const run = async () => {
    setRunning(true);
    try {
      const r = await endpoints.analyzeRisk({
        title: form.title,
        portfolio_value: Number(form.portfolio_value),
        simulations: Number(form.simulations),
      });
      setSelected(r.data);
      await load();
    } finally { setRunning(false); }
  };

  const distData = selected?.distribution?.map((v, i) => ({ i, v })) || [];
  const stressData = selected?.stress_results || [];

  return (
    <div className="p-4 md:p-10 max-w-[1500px] mx-auto">
      <PageHeader
        eyebrow="CLARA Risk"
        title="Quantitative Risk Engine"
        description="Institutional Monte Carlo simulations, VaR, stress tests and regulatory-style scenarios."
        testid="risk-page"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Runner */}
        <Card>
          <div className="overline mb-4">New Analysis</div>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-white/50 mb-1 block">Title</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="risk-title" />
            </div>
            <div>
              <label className="text-[11px] text-white/50 mb-1 block">Portfolio Value (€)</label>
              <Input type="number" value={form.portfolio_value} onChange={(e) => setForm({ ...form, portfolio_value: e.target.value })} data-testid="risk-portfolio" />
            </div>
            <div>
              <label className="text-[11px] text-white/50 mb-1 block">Monte Carlo Simulations</label>
              <Input type="number" value={form.simulations} onChange={(e) => setForm({ ...form, simulations: e.target.value })} />
              <div className="text-[10px] text-white/30 mt-1">Max 100,000</div>
            </div>
            <Button onClick={run} disabled={running} className="w-full justify-center" data-testid="run-risk-btn">
              {running ? <><Spinner size={13} /> Computing…</> : <><Play size={13} /> Run Simulation</>}
            </Button>
          </div>

          {analyses.length > 1 && (
            <div className="mt-6 pt-6 border-t border-white/5">
              <div className="overline mb-3">History</div>
              <div className="space-y-1">
                {analyses.slice(0, 6).map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setSelected(a)}
                    className={`w-full text-left px-2 py-1.5 rounded text-[12px] transition ${selected?.id === a.id ? "bg-white/[0.04] text-white" : "text-white/50 hover:bg-white/[0.02]"}`}
                  >
                    <div className="truncate">{a.title}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Results */}
        <div className="lg:col-span-2 space-y-5">
          {!selected && !running ? (
            <Card>
              <div className="text-center py-16">
                <Activity size={32} className="mx-auto text-white/20 mb-3" />
                <div className="text-white/40">Run your first risk analysis to see results.</div>
              </div>
            </Card>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                  <div className="overline mb-1">VaR 95%</div>
                  <div className="font-display text-xl text-red-400">{fmt.money(selected?.var_95)}</div>
                </Card>
                <Card>
                  <div className="overline mb-1">VaR 99%</div>
                  <div className="font-display text-xl text-red-400">{fmt.money(selected?.var_99)}</div>
                </Card>
                <Card>
                  <div className="overline mb-1">Expected Shortfall</div>
                  <div className="font-display text-xl text-orange-400">{fmt.money(selected?.expected_shortfall)}</div>
                </Card>
                <Card>
                  <div className="overline mb-1">MC Mean</div>
                  <div className="font-display text-xl text-gold">{fmt.money(selected?.monte_carlo_mean)}</div>
                </Card>
              </div>

              {/* Distribution chart */}
              <Card>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="overline">Monte Carlo Distribution</div>
                    <div className="font-display text-lg mt-1">{fmt.num(selected?.simulations)} simulations</div>
                  </div>
                </div>
                <div className="h-60">
                  <ResponsiveContainer>
                    <AreaChart data={distData} margin={{ top: 10, right: 0, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="distGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#C5A059" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#C5A059" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                      <XAxis dataKey="i" tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => fmt.money(v)} />
                      <Tooltip
                        contentStyle={{ background: "#18181b", border: "1px solid rgba(197,160,89,0.3)", borderRadius: "6px", fontSize: "12px" }}
                        formatter={(v) => [fmt.moneyFull(v), "Outcome"]}
                      />
                      <Area type="monotone" dataKey="v" stroke="#C5A059" strokeWidth={1.5} fill="url(#distGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Stress tests */}
              <Card>
                <div className="overline mb-3">Stress Scenarios</div>
                <div className="space-y-2">
                  {stressData.map((s, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`stress-${i}`}>
                      <div className="w-44 text-[12px] text-white/70">{s.scenario}</div>
                      <div className="flex-1 h-6 bg-white/5 rounded overflow-hidden relative">
                        <div
                          className="h-full"
                          style={{
                            width: `${(s.value / selected.portfolio_value) * 100}%`,
                            background: s.impact_pct === 0 ? "#C5A059" : s.impact_pct < -20 ? "#ef4444" : s.impact_pct < -10 ? "#f97316" : "#facc15",
                          }}
                        />
                      </div>
                      <div className="w-28 text-right text-[12px] font-mono">{fmt.money(s.value)}</div>
                      <div className="w-14 text-right text-[11px] font-mono" style={{ color: s.impact_pct === 0 ? "#C5A059" : "#ef4444" }}>
                        {s.impact_pct > 0 ? "+" : ""}{s.impact_pct}%
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* AI commentary */}
              {selected?.commentary && (
                <Card>
                  <div className="flex items-start gap-3">
                    <Sparkles size={18} className="text-gold mt-0.5 shrink-0" />
                    <div>
                      <div className="overline mb-2">Committee Commentary</div>
                      <div className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{selected.commentary}</div>
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
